import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * Hook for managing resizable column widths with maximum performance
 * 
 * Performance optimizations:
 * 1. Zero React re-renders during resize (all refs)
 * 2. CSSOM API for direct stylesheet rule manipulation (faster than textContent)
 * 3. requestAnimationFrame throttling (max 60fps updates)
 * 4. CSS containment hints (will-change, contain)
 * 5. Debounced localStorage writes
 * 
 * For tables with 10k+ rows, this provides smooth 60fps column resizing.
 * 
 * HOW IT WORKS:
 * - On resize start: creates a CSSStyleSheet with a rule targeting [data-col-id="X"]
 * - During resize: modifies the rule's cssText directly via CSSOM (no DOM manipulation)
 * - On resize end: removes stylesheet, updates React state once
 * 
 * Usage:
 * ```jsx
 * const {
 *   columnWidths,
 *   getColumnWidth,
 *   getResizeHandleProps,
 *   tableContainerRef,
 * } = useResizableColumns('my_table_widths');
 * 
 * <div ref={tableContainerRef}>
 *   <th data-col-id="col1" style={{ width: getColumnWidth('col1', 100) }}>
 *     Header
 *     <div {...getResizeHandleProps('col1', getColumnWidth('col1', 100))} />
 *   </th>
 *   <td data-col-id="col1" style={{ width: getColumnWidth('col1', 100) }}>Cell</td>
 * </div>
 * ```
 */
const useResizableColumns = (storageKey, defaultWidths = {}) => {
  const [columnWidths, setColumnWidths] = useState(() => {
    if (!storageKey) return {};
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) return JSON.parse(stored);
    } catch (e) { /* ignore */ }
    return {};
  });
  
  // ALL resize state is in refs - zero React re-renders during resize
  const resizingColumnRef = useRef(null);
  const resizeStartX = useRef(0);
  const resizeStartWidth = useRef(0);
  const pendingWidthRef = useRef(null);
  const rafIdRef = useRef(null);
  
  // CSSOM-based styling (faster than DOM manipulation)
  const styleSheetRef = useRef(null);
  const styleElementRef = useRef(null);
  
  // Ref to the table container
  const tableContainerRef = useRef(null);
  
  // Debounce timer for localStorage
  const saveTimerRef = useRef(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (styleElementRef.current) {
        styleElementRef.current.remove();
      }
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  // Debounced save to localStorage
  const saveColumnWidths = useCallback((widths) => {
    if (!storageKey) return;
    
    // Clear any pending save
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    
    // Debounce localStorage writes by 100ms
    saveTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(storageKey, JSON.stringify(widths));
      } catch (e) { /* ignore */ }
    }, 100);
  }, [storageKey]);

  const getColumnWidth = useCallback((columnKey, defaultWidth) => {
    return columnWidths[columnKey] || defaultWidths[columnKey] || defaultWidth || 100;
  }, [columnWidths, defaultWidths]);

  // Build the CSS rule text
  const buildCssRule = useCallback((columnKey, width) => {
    return `div[data-col-id="${columnKey}"] {
      width: ${width}px !important;
      min-width: ${width}px !important;
      max-width: ${width}px !important;
      overflow: hidden !important;
      will-change: width;
      contain: layout style;
    }`;
  }, []);

  // Create stylesheet with CSSOM for fastest updates
  // Uses requestAnimationFrame to avoid blocking the main thread on mousedown
  const createResizeStyle = useCallback((columnKey, width) => {
    // Remove existing style if any
    if (styleElementRef.current) {
      styleElementRef.current.remove();
      styleElementRef.current = null;
      styleSheetRef.current = null;
    }
    
    // Defer style creation to next frame to avoid blocking mousedown
    requestAnimationFrame(() => {
      // Create new style element
      const style = document.createElement('style');
      style.id = 'column-resize-style';
      document.head.appendChild(style);
      styleElementRef.current = style;
      
      // Get the CSSStyleSheet object for direct rule manipulation
      styleSheetRef.current = style.sheet;
      
      // Insert the rule
      if (styleSheetRef.current) {
        try {
          styleSheetRef.current.insertRule(buildCssRule(columnKey, width), 0);
        } catch (e) {
          // Fallback to textContent if CSSOM fails
          style.textContent = buildCssRule(columnKey, width);
          styleSheetRef.current = null;
        }
      }
    });
  }, [buildCssRule]);

  // Update the CSS rule using CSSOM (much faster than textContent)
  const updateResizeStyle = useCallback((columnKey, width) => {
    if (styleSheetRef.current && styleSheetRef.current.cssRules?.length > 0) {
      // CSSOM: Delete and re-insert rule (fastest way to update)
      try {
        styleSheetRef.current.deleteRule(0);
        styleSheetRef.current.insertRule(buildCssRule(columnKey, width), 0);
      } catch (e) {
        // Fallback
        if (styleElementRef.current) {
          styleElementRef.current.textContent = buildCssRule(columnKey, width);
        }
      }
    } else if (styleElementRef.current) {
      // Fallback to textContent
      styleElementRef.current.textContent = buildCssRule(columnKey, width);
    }
  }, [buildCssRule]);

  // Remove the dynamic style
  const removeResizeStyle = useCallback(() => {
    if (styleElementRef.current) {
      styleElementRef.current.remove();
      styleElementRef.current = null;
      styleSheetRef.current = null;
    }
  }, []);

  // Handle resize move - update CSS rule via rAF
  const handleResizeMove = useCallback((e) => {
    const columnKey = resizingColumnRef.current;
    if (!columnKey) return;
    
    const diff = e.clientX - resizeStartX.current;
    const newWidth = Math.max(40, resizeStartWidth.current + diff);
    pendingWidthRef.current = newWidth;
    
    // Cancel any pending rAF to prevent frame stacking
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    
    // Schedule CSS update for next frame
    rafIdRef.current = requestAnimationFrame(() => {
      updateResizeStyle(columnKey, newWidth);
    });
  }, [updateResizeStyle]);

  // End resize
  const handleResizeEnd = useCallback(() => {
    const columnKey = resizingColumnRef.current;
    const finalWidth = pendingWidthRef.current;
    
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    
    // Clean up refs and listeners FIRST (synchronous, fast)
    resizingColumnRef.current = null;
    pendingWidthRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    
    // IMPORTANT: Remove the CSS rule FIRST, before updating React state
    // This ensures React's inline styles take over immediately
    removeResizeStyle();
    
    if (columnKey && finalWidth !== null) {
      // Update React state - this will re-render with correct widths
      setColumnWidths(prev => {
        const updated = { ...prev, [columnKey]: finalWidth };
        saveColumnWidths(updated);
        return updated;
      });
    }
  }, [saveColumnWidths, handleResizeMove, removeResizeStyle]);

  // Start resize
  const handleResizeStart = useCallback((e, columnKey, currentWidth) => {
    e.preventDefault();
    e.stopPropagation();
    
    resizingColumnRef.current = columnKey;
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = currentWidth;
    pendingWidthRef.current = currentWidth;
    
    // Create the CSS rule immediately
    createResizeStyle(columnKey, currentWidth);
    
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
  }, [handleResizeMove, handleResizeEnd, createResizeStyle]);

  const resetColumnWidths = useCallback(() => {
    setColumnWidths({});
    if (storageKey) {
      try {
        localStorage.removeItem(storageKey);
      } catch (e) { /* ignore */ }
    }
  }, [storageKey]);

  const resetColumnWidth = useCallback((columnKey) => {
    setColumnWidths(prev => {
      const updated = { ...prev };
      delete updated[columnKey];
      saveColumnWidths(updated);
      return updated;
    });
  }, [saveColumnWidths]);

  const setColumnWidth = useCallback((columnKey, width) => {
    setColumnWidths(prev => {
      const updated = { ...prev, [columnKey]: width };
      saveColumnWidths(updated);
      return updated;
    });
  }, [saveColumnWidths]);

  const getResizeHandleProps = useCallback((columnKey, currentWidth) => ({
    onMouseDown: (e) => handleResizeStart(e, columnKey, currentWidth),
    onClick: (e) => e.stopPropagation(),
    style: {
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '5px',
      cursor: 'col-resize',
      backgroundColor: 'transparent',
      zIndex: 1,
    },
    onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = '#dee2e6'; },
    onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = 'transparent'; },
  }), [handleResizeStart]);

  return {
    columnWidths,
    tableContainerRef,
    getColumnWidth,
    handleResizeStart,
    getResizeHandleProps,
    resetColumnWidths,
    resetColumnWidth,
    setColumnWidth,
  };
};

export default useResizableColumns;
